/* eslint-disable react/jsx-handler-names */
import React, { FormEvent } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { getErrorMessageController } from 'utils/react-hook-form'

/**
 * Defines the props for the MyTextField component
 */
type TextFieldProps = {
  name: string,
  ariaLabel?: string,
  label?: React.ReactNode,
  type?: string,
  inputMode?: 'text' | 'numeric' | 'none' | 'tel' | 'url' | 'email' | 'decimal' | 'search';
  multiline?: boolean
  className?: string;
  fieldClassName?: string;
  labelClassName?: string;
  errorMessageClassName?: string;
  autoComplete?: string;
  placeholder?: string;
  isCurrentlyValid?: boolean;
  maxLength?: number;
  addErrorPrefix?: boolean;
  onBlur?: () => void
  /**
   * Defines a pattern to replace when input is given to the textfield.
   * This is usually similar, but different than pattern validation
   * rules. For instance, this regex might be /^a/g meaning to replace
   * all characters that are not 'a', while the matching validation
   * rule would be /^[a]+$/, meaning that it must start and only
   * contain a's
   */
  pattern?: RegExp | string
  } & Omit<Parameters<typeof Controller>[0], 'render' | 'control'>

type TextFieldFormEvent = FormEvent<HTMLInputElement> & FormEvent<HTMLTextAreaElement>

export const MyTextField: React.FC<TextFieldProps> = ({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  className,
  label,
  labelClassName,
  inputMode,
  fieldClassName,
  pattern,
  errorMessageClassName,
  multiline,
  ariaLabel,
  isCurrentlyValid,
  addErrorPrefix, // TODO - set this automatically to AddErrorPrefix = true, once MCF has this added in
  onBlur,
  ...props
}) => {
  const { control } = useFormContext()
  return (
    <Controller
      rules={rules}
      control={control}
      name={name}
      shouldUnregister={shouldUnregister}
      defaultValue={defaultValue}
      render={({ field, fieldState }) => {
        const { ref } = field
        const ComponentType = multiline ? 'textarea' : 'input'
        return (
          // Don't use TextField here because we do not want role=alert in our error messages
          <div className={className} >
            <label
              className={`ds-c-label ${labelClassName ?? ''}`} htmlFor={name}
            ><span>{label}</span>
            </label>
            <div
              className={`field-wrapper ds-c-field ${fieldClassName ?? ''} ${fieldState.error || isCurrentlyValid === false ? 'ds-c-field--error' : ''}`}
            >
              <ComponentType
                // {...field}
                aria-describedby={fieldState.error ? `error-${field.name}` : undefined}
                name={field.name}
                ref={ref}
                onChange={field.onChange}
                onInput={(event: TextFieldFormEvent) => {
                  if (pattern) {
                    // Replace characters and maintain the cursor position in the field
                    const cursorStart = event.currentTarget.selectionStart
                    let charactersRemoved = 0
                    event.currentTarget.value = event.currentTarget.value.replace(
                      pattern,
                      () => {
                        charactersRemoved++
                        return ''
                      }
                    )
                    const newCursorStart = cursorStart - charactersRemoved
                    event.currentTarget.selectionStart = newCursorStart
                    event.currentTarget.selectionEnd = newCursorStart
                  }
                }}
                onBlur={() => {
                  onBlur?.()
                  field.onBlur()
                }}
                value={field.value}
                id={name}
                className='field-wrapper__input'
                aria-required={!!rules?.required}
                aria-invalid={!!fieldState.error}
                aria-label={ariaLabel}
                {...props}
              />
            </div>
            {(fieldState.error || isCurrentlyValid === false) && (
              <span id={`error-${field.name}`} className={`error-message ds-c-field__error-message ${errorMessageClassName ?? ''}`}>
                {getErrorMessageController(fieldState, addErrorPrefix)}
              </span>
            )}
          </div>
        )
      }}
    />
  )
}
